import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import type { Query, QueryClient } from "@tanstack/react-query"
import { type PersistedClient, persistQueryClient } from "@tanstack/react-query-persist-client"

export const DASHBOARD_QUERY_CACHE_KEY = "playbrain-dashboard-query-cache"

/**
 * How long a restored list stays usable.
 *
 * It is only ever shown while a fresh copy is on its way, so this is a ceiling
 * on how stale the first paint may look, not on how stale the data can be.
 */
const DASHBOARD_QUERY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

/**
 * Bump to abandon every cached entry.
 *
 * The shape of what gets stored is part of this module's contract with itself;
 * change the shape without changing this and an old entry deserializes into
 * the new reader.
 *
 * Bumped to 2 to discard entries written before the list became a projection.
 * Those rows carried whole record bodies, including unpublished drafts —
 * exactly what the note below says is never written here — so they are not
 * merely stale, they are the thing being fixed, and they have to go rather
 * than wait out their day-long expiry on someone's disk.
 */
const DASHBOARD_QUERY_CACHE_VERSION = "2"

/**
 * The one query family worth keeping on disk.
 *
 * The content list is what a section paints first and what a reader waits on,
 * and its rows are metadata — titles, statuses, timestamps. Document details
 * are deliberately excluded: those carry the full body of unpublished drafts,
 * and there is no first-paint argument for writing them to a disk that may be
 * a shared office machine. Users and roles are excluded for the same reason.
 *
 * That distinction only holds because the list is a projection. It once built
 * its rows from the editor's own loader, which put every article body and job
 * description into this store despite the paragraph above — so if a list row
 * ever starts carrying a record's body again, this store stops being safe to
 * write. `cmsRecordSummaryValues.ts` is where that boundary is kept.
 */
const PERSISTED_QUERY_PREFIX = "dashboard-content-documents"

function shouldPersistQuery(query: Query) {
  const [family] = query.queryKey
  return family === PERSISTED_QUERY_PREFIX && query.state.status === "success"
}

/**
 * Restores the last-seen content lists, then keeps them up to date.
 *
 * This is the client-side half of what makes a section feel instant. The route
 * loader already delivers the rows with the document on a cold load; this
 * covers the visit after that one, where the table can paint from disk while
 * the server is still being asked.
 *
 * Restoring cannot clobber fresher data: react-query's hydration only replaces
 * a query whose stored copy is newer than the one in memory, so the rows the
 * loader just delivered win over anything held here.
 *
 * A no-op outside the browser, and wherever storage is unavailable — Safari's
 * private mode throws on write rather than refusing politely, and a dashboard
 * that will not load is a worse outcome than one that does not remember.
 */
export function startDashboardQueryPersistence(queryClient: QueryClient) {
  const storage = safeLocalStorage()
  if (!storage) return () => undefined

  const [unsubscribe] = persistQueryClient({
    buster: DASHBOARD_QUERY_CACHE_VERSION,
    dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
    maxAge: DASHBOARD_QUERY_CACHE_MAX_AGE_MS,
    persister: createSyncStoragePersister({
      deserialize: restoreAsOutOfDate,
      key: DASHBOARD_QUERY_CACHE_KEY,
      storage,
    }),
    queryClient,
  })

  return unsubscribe
}

/**
 * Reads the stored lists back already marked out of date.
 *
 * A restored list is a first paint, never an answer, and how old it is decides
 * nothing. Writes here are throttled, and leaving the page cancels the pending
 * one — so a save that changed a list, possibly deleting the version a row
 * points at, may never reach the disk when the reader goes straight to another
 * page. Read back as stored, a list fetched seconds before that save is inside
 * its staleTime: it is shown as current and not asked for again, and its rows
 * open editors for records that no longer exist. Marked out of date, it paints
 * and is refetched at once, which is the promise made above.
 */
function restoreAsOutOfDate(cached: string): PersistedClient {
  const client = JSON.parse(cached) as PersistedClient
  for (const query of client.clientState.queries) query.state.isInvalidated = true
  return client
}

/**
 * Forgets everything, for sign-out and for a session that has expired.
 *
 * Leaving one person's section titles on the machine for the next person to
 * sign in is exactly the kind of thing a cache is not allowed to do.
 */
export function clearDashboardQueryCache() {
  safeLocalStorage()?.removeItem(DASHBOARD_QUERY_CACHE_KEY)
}

function safeLocalStorage() {
  if (typeof window === "undefined") return null
  try {
    const storage = window.localStorage
    const probe = `${DASHBOARD_QUERY_CACHE_KEY}-probe`
    storage.setItem(probe, "1")
    storage.removeItem(probe)
    return storage
  } catch {
    return null
  }
}
